import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { TenantService } from './tenant.service';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { IntegrationDetails } from '../models/integration-details';
import { SolutionHistory } from '../models/solution-history';
import { UserService } from './user.service';
import { IntegrationStatus } from '../models/integration-status';
import { ConfigService } from './config.service';
import { BYPASS_LOG } from './http-oidc-interceptor.service';
import { ActionService } from './action.service';
import { SignalRConnection } from '../models/signal-rconnection';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { DataLakeService } from './data-lake.service';
import { take } from 'rxjs/operators';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class BusinessLogicService {
  private readonly logger = inject(LoggerService);
  public $integrations = new BehaviorSubject<IntegrationDetails[]>(null);
  public $useLocalhost: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private integrationHistories: SolutionHistory[] = [];
  private user: any;
  private servicesUrl: string;
  private localHostUrl: string;
  private integrationNameSelected;
  private signalRCloseRequested: boolean = false;
  private hubConnection: HubConnection;
  private readonly message$: Subject<string>;
  public messageObservable$: Observable<string>;
  public connected: boolean = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly tenantService: TenantService,
    private readonly userService: UserService,
    private readonly configService: ConfigService,
    private readonly actionService: ActionService,
    private readonly dataLakeService: DataLakeService,
    private readonly ngZone: NgZone
  ) {
    this.message$ = new Subject<string>();
    this.messageObservable$ = this.message$.asObservable();
    this.tenantService.$tenant.subscribe(tenant => {
      if (tenant != null) {
        this.localHostUrl = tenant.solutionDevUrl;
        this.$integrations.next(tenant.integrations);
        tenant.integrations.forEach(i => {
          this.integrationHistories = this.integrationHistories.concat(new SolutionHistory(i.name));
        });
        this.$useLocalhost.subscribe(_ => {
          this.startSignalR();
        });
        this.actionService.$integrationNameSelected.subscribe(integrationName => {
          this.integrationNameSelected = integrationName;
          this.loadIntegrationHistory(integrationName);
        });
      }
    });
    this.configService.$configuration.subscribe(config => {
      if (config?.servicesUrl != null) {
        this.servicesUrl = config.servicesUrl;
      }
    })
    this.userService.$user.subscribe(user => {
      if (user != null) {
        this.user = user;
      }
    });
  }

  public runIntegration(integrationName: string, actionName: string, params: any[]) {
    const integration = this.$integrations.value.find(i => i.name == integrationName);
    const url = this.$useLocalhost.value
      ? `${this.tenantService.$tenant.value.solutionDevUrl}/${integration.solutionUrlExtension}`
      : `${this.businessLogicEndpoint()}/start/${integration.name}`;
    const action = integration.actions.find(a => a.name == actionName);
    if (action != null) {
      let postBody = this.buildIntegrationPostBody(integrationName, actionName, params);
      if (this.$useLocalhost.value) {
        firstValueFrom(this.httpClient.post<any>(url, postBody, { context: new HttpContext().set(BYPASS_LOG, true) })).then(async _ => {
          this.loadIntegrationHistory(integrationName);
        });
      }
      else {
        firstValueFrom(this.httpClient.post<any>(url, postBody)).then(async _ => {
          this.loadIntegrationHistory(integrationName);
        });
      }
    }
  }

  public getIntegrationHistoryBehaviorSubject(integrationName: string): BehaviorSubject<IntegrationStatus[]> {
    return this.integrationHistories.find(integrationHistory => integrationHistory.name == integrationName).statusListing;
  }

  public async loadIntegrationHistory(integrationName: string) {
    const integrationDetails = this.$integrations.value.find(i => i.name == integrationName)
    if (integrationDetails != null) {
      if (integrationDetails.solutionUrlExtension != null) {
        const url = this.$useLocalhost.value
          ? `${this.tenantService.$tenant.value.solutionDevUrl}/api/DurableInstances/${integrationName}`
          : `${this.businessLogicEndpoint()}/instanceInfo/${integrationName}`;
        if (this.$useLocalhost.value) {
          firstValueFrom(this.httpClient.get<any[]>(url, { context: new HttpContext().set(BYPASS_LOG, true) })).then(s => {
            const status: IntegrationStatus[] = this.buildIntegrationHistory(s, integrationName);
            this.integrationHistories.find(iH => iH.name == integrationName).statusListing.next(status);
          });
        }
        else {
          firstValueFrom(this.httpClient.get<any[]>(url)).then(s => {
            const status: IntegrationStatus[] = this.buildIntegrationHistory(s, integrationName);

            this.integrationHistories.find(iH => iH.name == integrationName).statusListing.next(status);
          });
        }
      }
    }
  }

  private buildIntegrationHistory(businessLogicHistory: any[], integrationName: string): IntegrationStatus[] {
    let status: IntegrationStatus[] = [];
    if (businessLogicHistory != null) {
      businessLogicHistory.forEach(stat => {
        const newStatus = JSON.parse(stat);
        status = status.concat(newStatus);
      });
      status = status.sort((a, b) => {
        return Date.parse(b.createdTime) - Date.parse(a.createdTime);
      });
      status.forEach(s => {
        s.alias = integrationName;
      });
    }
    return status;
  }

  // Deletes a specific execution instance from the history of a durable function from the server.
  public async deleteInstance(instanceId: string, alias: string, name: string) {
    const url = this.$useLocalhost.value
      ? `${this.tenantService.$tenant.value.solutionDevUrl}/api/DeleteInstance/${alias}/${instanceId}`
      : `${this.businessLogicEndpoint()}/delete/${alias}/${instanceId}`;
    if (this.$useLocalhost.value) {
      await firstValueFrom(this.httpClient.get<any>(url, { context: new HttpContext().set(BYPASS_LOG, true) })).then(async _ => {
        await this.loadIntegrationHistory(alias);
      });
    }
    else {
      await firstValueFrom(this.httpClient.post<any>(url, null)).then(async _ => {
        await this.loadIntegrationHistory(alias);
      });
    }
  }

  // Terminates the execution of a running instance a durable function.
  public async terminateInstance(instanceId: string, alias: string, name: string) {
    const url = this.$useLocalhost.value
      ? `${this.tenantService.$tenant.value.solutionDevUrl}/api/TerminateInstance/${instanceId}`
      : `${this.businessLogicEndpoint()}/stop/${instanceId}`;
    if (this.$useLocalhost.value) {
      await firstValueFrom(this.httpClient.get<any>(url, { context: new HttpContext().set(BYPASS_LOG, true) })).then(async _ => {
        await this.delay(2000);
        await this.loadIntegrationHistory(alias);
      });
    }
    else {
      await firstValueFrom(this.httpClient.post<any>(url, null)).then(async _ => {
        await this.delay(2000);
        await this.loadIntegrationHistory(alias);
      });
    }
  }

  public listIntegrations(): IntegrationDetails[] {
    return this.$integrations.getValue();
  }

  public toggleLocalHost(useLocalhost: boolean) {
    this.$useLocalhost.next(useLocalhost)
    this.loadIntegrationHistory(this.integrationNameSelected);
  }

  private buildIntegrationPostBody(integrationName: string, actionName: string, params: any[]): any {
    const postBody = {};
    params.forEach(p => {
      //Param types:
      // 1 - String
      // 2 - Integer
      // 3 - Decimal
      // 4 - Boolean
      // 5 - DateTime
      //If values are not null, pass as-is
      if (p.value != null) {
        if (p.dataType != 5) {
          postBody[`${p.name}`] = `${p.value}`;
        } else {
          let date = p.value.toISOString();
          postBody[`${p.name}`] = `${date}`;
        }
        // If values ARE null, pass the default value  
      } else if (p.defaultValue) {
        postBody[`${p.name}`] = `${p.defaultValue}`;
      } else {
        postBody[`${p.name}`] = p.dataType != 4 ? `${null}` : `${false}`;
      }
    });
    if (this.tenantService.$tenant.value != null) {
      const tenant = this.tenantService.$tenant.value;
      postBody["solutionName"] = integrationName;
      postBody["userName"] = this.user.name;
      postBody["userEmail"] = this.user.email;
      postBody["tenantId"] = this.tenantService.$tenant.value.id;
      postBody["actionName"] = actionName;
      postBody["culture"] = this.user.culture;
    }
    return postBody;
  }

  // Arbitrary delay timer
  private delay(delay: number) {
    return new Promise(r => {
      setTimeout(r, delay);
    });
  }

  public businessLogicEndpoint(): string {
    return `${this.servicesUrl}/api/${this.user["clientNameSpace"]}/businessLogic`;
  }

  ////////////////////SIGNALR
  public async startSignalR(): Promise<void> {
    const negotiateUrl = this.$useLocalhost.value
      ? `${this.tenantService.$tenant.value.solutionDevUrl}/api/negotiate`
      : `${this.businessLogicEndpoint()}/negotiate`;

    await this.stopSignalR();
    this.logger.info('BusinessLogicService', 'Starting SignalR connection', {
      operation: 'startSignalR',
      endpointPath: negotiateUrl
    });

    // take(1) ensures we only use the current tenant value once for this SignalR start
    // This prevents accumulating subscriptions/handlers if startSignalR() is called multiple times,
    // which can otherwise lead to duplicate notify events, extra HTTP negotiate calls, and leaks
    this.tenantService.$tenant.pipe(take(1)).subscribe(t => {
      if (t != null) {
        let context = new HttpContext();
        if (this.$useLocalhost.value) {
          // HttpContext is immutable; set() returns a new instance
          context = context.set(BYPASS_LOG, true);
        }

        this.httpClient.get<SignalRConnection>(negotiateUrl, { context }).subscribe(info => {
          void this.initializeHubConnection(info);
        });
      }
    });
  }

  private async initializeHubConnection(connectionInfo: SignalRConnection): Promise<void> {
    const options: signalR.IHttpConnectionOptions = {
      accessTokenFactory: () => connectionInfo.accessToken
    };
    this.announceMessage('User received SignalR JWT.');

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(connectionInfo.url, options)
      .configureLogging(signalR.LogLevel.Information)
      .build();

    try {
      await this.hubConnection.start();
      this.handleHubStarted();
    } catch (err) {
      this.handleHubStartError(err);
    }

    this.hubConnection.on('notify', (data: any) => this.handleNotify(data));
    this.hubConnection.onclose((error) => this.handleHubClosed(error));
  }

  private handleHubStarted(): void {
    this.ngZone.run(() => {
      this.announceMessage('Hub Connection Started.');
      this.connected = true;
    });
  }

  private handleHubStartError(err: any): void {
    this.ngZone.run(() => {
      this.announceErrorMessage(err);
      this.connected = false;
    });
  }

  private handleNotify(data: any): void {
    this.ngZone.run(() => {
      const parsedData = JSON.parse(data);
      const solutionName = parsedData["solutionName"];
      const message = parsedData["message"];

      this.logger.debug('BusinessLogicService', 'Received SignalR notification', {
        operation: 'handleNotify',
        solutionName,
        message
      });

      if (message == "Process Started") {
        void this.loadIntegrationHistory(solutionName);
      } else if (message == "Process Completed" || message == "Process Failed") {
        void this.handleProcessCompleted(solutionName);
      }
    });
  }

  private async handleProcessCompleted(solutionName: string): Promise<void> {
    await this.delay(5000);
    await this.loadIntegrationHistory(solutionName);
    this.dataLakeService.loadDataLakeRunDataSolutionNames();
    this.dataLakeService.loadOutputSolutionNames();
  }

  private handleHubClosed(error: any): void {
    this.ngZone.run(() => {
      if (this.signalRCloseRequested) {
        this.signalRCloseRequested = false;
        this.connected = false;
        return;
      }

      this.connected = false;
      this.restartHubConnection();

      if (error) {
        this.announceErrorMessage(error);
        this.connected = false;
      }
    });
  }

  private restartHubConnection(): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.start()
      .then(() => {
        this.announceMessage('Hub Connection Restarted.');
        this.connected = true;
      })
      .catch(err => {
        this.announceErrorMessage(err);
        this.connected = false;
      });
  }

  // Close websockets connection
  public async stopSignalR(): Promise<void> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      this.signalRCloseRequested = true;
      this.hubConnection.off('notify');
      this.connected = false;
      return await this.hubConnection.stop();
    }
  }

  private announceMessage(message: string): void {
    this.message$.next(message);
  }

  private announceErrorMessage(content: any): void {
    if (content instanceof HttpErrorResponse || content instanceof Error) {
      this.announceMessage(`Error: ${content.message}`);
    } else {
      this.announceMessage(content);
    }
  }
}
